import {
  cloneProgressState,
  createDefaultProgressState,
  type ProgressState,
} from "../application/progress-state";
import type { ProgressMutation } from "../application/progress-mutation";
import { progressConfig } from "../config/progress-config";
import {
  queueRoundSettlementProgress,
  settleRoundProgress,
  type RoundSettlementQueueStatus,
  type RoundSettlementReceipt,
  type RoundSettlementStatus,
} from "../application/round-settlement";
import {
  purchaseFoodValue,
  purchaseMovementSpeed,
  purchaseStartingSize,
  type FoodValuePurchaseStatus,
  type MovementSpeedPurchaseStatus,
  type StartingSizePurchaseStatus,
} from "../application/upgrade-rules";
import {
  selectSkin,
  unlockSkin,
  type SkinSelectionStatus,
  type SkinUnlockStatus,
} from "../application/skin-rules";
import {
  selectArenaBackground,
  type ArenaBackgroundSelectionStatus,
} from "../application/arena-background-rules";
import {
  decodeProgress,
  sanitizeProgress,
  serializeProgress,
} from "./progress-codec";

export interface ProgressStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): unknown;
}

const isDurabilityPromise = (value: unknown): value is Promise<boolean> =>
  value instanceof Promise;

export interface ProgressStorageScope {
  readonly localStorage?: ProgressStorage;
}

export type StoredRoundSettlementStatus = RoundSettlementStatus | "failed";
export type StoredRoundSettlementQueueStatus =
  RoundSettlementQueueStatus | "failed";
export type StoredStartingSizePurchaseStatus =
  StartingSizePurchaseStatus | "persistence-failed";
export type StoredMovementSpeedPurchaseStatus =
  MovementSpeedPurchaseStatus | "persistence-failed";
export type StoredFoodValuePurchaseStatus =
  FoodValuePurchaseStatus | "persistence-failed";
export type StoredSkinUnlockStatus = SkinUnlockStatus | "persistence-failed";
export type StoredSkinSelectionStatus =
  SkinSelectionStatus | "persistence-failed";
export type StoredArenaBackgroundSelectionStatus =
  ArenaBackgroundSelectionStatus | "persistence-failed";

const unavailableStorage: ProgressStorage = {
  getItem: () => {
    throw new Error("Local storage is unavailable");
  },
  setItem: () => {
    throw new Error("Local storage is unavailable");
  },
};

export class LocalProgressStore {
  private durableRaw: string | null = null;
  private durableState = createDefaultProgressState();
  private durableWriteLocked = false;
  private hasRawBaseline = false;
  private latestWriteSequence = 0;
  private loaded = false;
  private raw: string | null = null;
  private state = createDefaultProgressState();
  private writeLocked = false;

  public constructor(private readonly storage: ProgressStorage) {}

  public load(): ProgressState {
    let raw: string | null;
    try {
      raw = this.storage.getItem(progressConfig.storageKey);
    } catch {
      if (!this.loaded) {
        this.loaded = true;
      }
      return this.getState();
    }

    const decoded = decodeProgress(raw);
    this.loaded = true;
    this.hasRawBaseline = true;
    this.raw = raw;
    this.state = decoded.state;
    this.writeLocked = decoded.writeLocked;
    this.durableRaw = raw;
    this.durableState = decoded.state;
    this.durableWriteLocked = decoded.writeLocked;

    if (decoded.needsRepair) {
      const repairedRaw = serializeProgress(decoded.state);
      const repair = this.persistState(decoded.state, repairedRaw);
      if (repair instanceof Promise) {
        void repair;
      }
    }

    return this.getState();
  }

  public getState(): ProgressState {
    return cloneProgressState(this.state);
  }

  public isWriteLocked(): boolean {
    return this.writeLocked;
  }

  public save(nextState: ProgressState): ProgressMutation<boolean> {
    if (!this.ensureLoadedForWrite()) {
      return false;
    }
    if (this.writeLocked) {
      return false;
    }

    let currentRaw: string | null;
    try {
      currentRaw = this.storage.getItem(progressConfig.storageKey);
    } catch {
      return false;
    }

    if (!this.hasRawBaseline || currentRaw !== this.raw) {
      if (decodeProgress(currentRaw).writeLocked) {
        this.hasRawBaseline = true;
        this.raw = currentRaw;
        this.writeLocked = true;
      }
      return false;
    }

    const state = sanitizeProgress(nextState);
    const raw = serializeProgress(state);
    return this.persistState(state, raw);
  }

  public update(
    createNextState: (state: ProgressState) => ProgressState,
  ): ProgressMutation<boolean> {
    if (!this.ensureLoadedForWrite()) {
      return false;
    }
    return this.save(createNextState(this.getState()));
  }

  public settleRound(
    receipt: RoundSettlementReceipt,
  ): ProgressMutation<StoredRoundSettlementStatus> {
    if (!this.ensureLoadedForWrite()) {
      return "failed";
    }
    const state = this.getState();
    const transition = settleRoundProgress(state, receipt);
    if (transition.status === "conflict" || transition.state === state) {
      return transition.status;
    }
    return this.mapPersistence(
      this.save(transition.state),
      transition.status,
      "failed",
    );
  }

  public queueRound(
    receipt: RoundSettlementReceipt,
  ): ProgressMutation<StoredRoundSettlementQueueStatus> {
    if (!this.ensureLoadedForWrite()) {
      return "failed";
    }
    const state = this.getState();
    const transition = queueRoundSettlementProgress(state, receipt);
    if (transition.status !== "queued" || transition.state === state) {
      return transition.status;
    }
    return this.mapPersistence(this.save(transition.state), "queued", "failed");
  }

  public purchaseStartingSize(
    expectedLevel: number,
  ): ProgressMutation<StoredStartingSizePurchaseStatus> {
    if (!this.ensureLoadedForWrite()) {
      return "persistence-failed";
    }
    if (this.writeLocked) {
      return "persistence-failed";
    }
    const state = this.getState();
    const transition = purchaseStartingSize(state, expectedLevel);
    if (transition.status !== "purchased" || transition.state === state) {
      return transition.status;
    }
    return this.mapPersistence(
      this.save(transition.state),
      "purchased",
      "persistence-failed",
    );
  }

  public purchaseMovementSpeed(
    expectedLevel: number,
  ): ProgressMutation<StoredMovementSpeedPurchaseStatus> {
    if (!this.ensureLoadedForWrite()) {
      return "persistence-failed";
    }
    if (this.writeLocked) {
      return "persistence-failed";
    }
    const state = this.getState();
    const transition = purchaseMovementSpeed(state, expectedLevel);
    if (transition.status !== "purchased" || transition.state === state) {
      return transition.status;
    }
    return this.mapPersistence(
      this.save(transition.state),
      "purchased",
      "persistence-failed",
    );
  }

  public purchaseFoodValue(
    expectedLevel: number,
  ): ProgressMutation<StoredFoodValuePurchaseStatus> {
    if (!this.ensureLoadedForWrite()) {
      return "persistence-failed";
    }
    if (this.writeLocked) {
      return "persistence-failed";
    }
    const state = this.getState();
    const transition = purchaseFoodValue(state, expectedLevel);
    if (transition.status !== "purchased" || transition.state === state) {
      return transition.status;
    }
    return this.mapPersistence(
      this.save(transition.state),
      "purchased",
      "persistence-failed",
    );
  }

  public unlockSkin(
    id: string,
    expectedCoins: number,
  ): ProgressMutation<StoredSkinUnlockStatus> {
    if (!this.ensureLoadedForWrite() || this.writeLocked) {
      return "persistence-failed";
    }
    const state = this.getState();
    const transition = unlockSkin(state, id, expectedCoins);
    if (transition.status !== "unlocked" || transition.state === state) {
      return transition.status;
    }
    return this.mapPersistence(
      this.save(transition.state),
      "unlocked",
      "persistence-failed",
    );
  }

  public selectSkin(
    id: string,
    expectedSelectedSkinId: string,
  ): ProgressMutation<StoredSkinSelectionStatus> {
    if (!this.ensureLoadedForWrite() || this.writeLocked) {
      return "persistence-failed";
    }
    const state = this.getState();
    const transition = selectSkin(state, id, expectedSelectedSkinId);
    if (transition.status !== "selected" || transition.state === state) {
      return transition.status;
    }
    return this.mapPersistence(
      this.save(transition.state),
      "selected",
      "persistence-failed",
    );
  }

  public selectArenaBackground(
    id: string,
    expectedSelectedId: string,
  ): ProgressMutation<StoredArenaBackgroundSelectionStatus> {
    if (!this.ensureLoadedForWrite() || this.writeLocked) {
      return "persistence-failed";
    }
    const state = this.getState();
    const transition = selectArenaBackground(state, id, expectedSelectedId);
    if (transition.status !== "selected" || transition.state === state) {
      return transition.status;
    }
    return this.mapPersistence(
      this.save(transition.state),
      "selected",
      "persistence-failed",
    );
  }

  public reset(confirmed: boolean): ProgressMutation<boolean> {
    if (!confirmed) {
      return false;
    }
    const state = createDefaultProgressState();
    const raw = serializeProgress(state);
    const persisted = this.persistState(state, raw);
    if (persisted !== false) {
      this.hasRawBaseline = true;
      this.loaded = true;
    }
    return persisted;
  }

  private mapPersistence<Success, Failure>(
    saved: ProgressMutation<boolean>,
    success: Success,
    failure: Failure,
  ): ProgressMutation<Failure | Success> {
    const map = (durable: boolean): Failure | Success =>
      durable ? success : failure;
    return saved instanceof Promise ? saved.then(map) : map(saved);
  }

  private persistState(
    state: ProgressState,
    raw: string,
  ): ProgressMutation<boolean> {
    let persisted: unknown;
    try {
      persisted = this.storage.setItem(progressConfig.storageKey, raw);
    } catch {
      return false;
    }

    this.latestWriteSequence += 1;
    const sequence = this.latestWriteSequence;
    this.raw = raw;
    this.state = state;
    this.writeLocked = false;
    if (!isDurabilityPromise(persisted)) {
      this.confirmDurableState(state, raw);
      return true;
    }
    return persisted.then(
      (durable) => {
        if (durable) {
          this.confirmDurableState(state, raw);
        } else if (sequence === this.latestWriteSequence) {
          this.restoreDurableState();
        }
        return durable;
      },
      () => {
        if (sequence === this.latestWriteSequence) {
          this.restoreDurableState();
        }
        return false;
      },
    );
  }

  private confirmDurableState(state: ProgressState, raw: string): void {
    this.durableRaw = raw;
    this.durableState = state;
    this.durableWriteLocked = false;
  }

  private restoreDurableState(): void {
    this.raw = this.durableRaw;
    this.state = this.durableState;
    this.writeLocked = this.durableWriteLocked;
  }

  private ensureLoadedForWrite(): boolean {
    if (this.loaded) {
      return true;
    }
    let raw: string | null;
    try {
      raw = this.storage.getItem(progressConfig.storageKey);
    } catch {
      return false;
    }
    const decoded = decodeProgress(raw);
    this.hasRawBaseline = true;
    this.loaded = true;
    this.raw = raw;
    this.state = decoded.state;
    this.writeLocked = decoded.writeLocked;
    return true;
  }
}

export const loadBrowserProgress = (
  scope: ProgressStorageScope,
): LocalProgressStore => {
  let storage: ProgressStorage;
  try {
    storage = scope.localStorage ?? unavailableStorage;
  } catch {
    storage = unavailableStorage;
  }
  const store = new LocalProgressStore(storage);
  store.load();
  return store;
};
